/**
 * Fall-stage props: boulder, gem, shield (bonus) and the movement-range buoy.
 * Each drawer paints into a canvas of the replaced PNG's logical size (w × h).
 *
 * Fit notes (physics reads only the frame aspect, never the art):
 *   boulder  drawn w×h on a collider circle of diameter w, and it tumbles; light rotates with
 *            it, so the facets read as a turning rock. Debris draws this frame squashed square.
 *   gem      Pixi and the Canvas object draw it into a square of min(w,h) (stretched 1.13×
 *            vertically) while the DOM shows it with object-fit: contain, so its proportions
 *            are designed halfway between (squashed 0.94× here) and both read fine.
 *   shield   letterboxed, undistorted.
 *   buoy     shown ~34 px tall at 50–100% alpha: few, bold shapes.
 */
import { INK } from "../palette.js";
import { TAU, glint, inkStyle, sparklePath } from "../toon-kit.js";
import { mapPts, polyPath, roundRectPath, roundedPolyPath } from "./prop-kit.js";

// ---- boulder -------------------------------------------------------------------------------

var STONE = {
  light: "#d9cdbf",
  base: "#ad9f93",
  shade: "#8a7b70",
  deep: "#6a5c53",
  ridge: "#f1e7d8",
  speck: "#8f8176",
};
var MOSS = { base: "#79c957", light: "#b9ec85", shade: "#4c963a", ink: "#2f6a2a" };

/** Rock outline in unit space (x right, y down), chunky and a little lopsided. */
var ROCK = [
  [-0.96, 0.06], [-0.84, -0.52], [-0.4, -0.92], [0.18, -0.97], [0.7, -0.74],
  [0.98, -0.16], [0.88, 0.48], [0.44, 0.92], [-0.24, 0.96], [-0.76, 0.7],
];
/**
 * Planes of the chunk, lit from the upper left: a bright top plane, the front plane (base,
 * where the face sits) and a shaded right plane. Their inner edges stay clear of the face.
 */
var ROCK_TOP = [[-1.3, -0.12], [-0.46, -0.4], [0.22, -0.5], [0.74, -0.64], [1.3, -0.5], [1.3, -1.3], [-1.3, -1.3]];
var ROCK_RIGHT = [[0.74, -0.64], [0.64, 0.02], [0.5, 0.56], [0.26, 1.3], [1.3, 1.3], [1.3, -0.5]];

function linePath(c, pts) {
  for (var i = 0; i < pts.length; i++) {
    if (i) c.lineTo(pts[i][0], pts[i][1]); else c.moveTo(pts[i][0], pts[i][1]);
  }
}

/** boulder.png 256×218 */
export function drawBoulder(c, w, h) {
  var cx = w * 0.5, cy = h * 0.505;
  var sx = w * 0.462, sy = h * 0.45;
  var line = Math.min(w, h) * 0.042;
  var pts = mapPts(ROCK, sx, sy, cx, cy);
  var corner = Math.min(w, h) * 0.08;
  function rock(cc) { roundedPolyPath(cc, pts, corner); }

  c.save();
  c.lineJoin = "round";
  c.lineCap = "round";
  c.beginPath();
  rock(c);
  c.fillStyle = STONE.base;
  c.fill();
  c.save();
  c.clip();
  // Planes: hard-edged tones, no seams; a lit ridge where the top plane turns to the front.
  c.beginPath();
  polyPath(c, ROCK_TOP, sx, sy, cx, cy);
  c.fillStyle = STONE.light;
  c.fill();
  c.beginPath();
  polyPath(c, ROCK_RIGHT, sx, sy, cx, cy);
  c.fillStyle = STONE.shade;
  c.fill();
  c.beginPath();
  linePath(c, mapPts(ROCK_TOP.slice(0, 4), sx, sy, cx, cy));
  c.strokeStyle = STONE.ridge;
  c.lineWidth = line * 0.55;
  c.stroke();
  // Shade crescent along the lower-right rim: the rock minus itself shifted up-left.
  c.beginPath();
  c.rect(0, 0, w, h);
  c.save();
  c.translate(-sx * 0.09, -sy * 0.12);
  rock(c);
  c.restore();
  c.fillStyle = STONE.deep;
  c.fill("evenodd");
  // Speckles: pits in the stone, each with a lit lower lip.
  var specks = [[-0.62, 0.36, 0.055], [-0.44, 0.6, 0.035], [0.7, 0.32, 0.045], [0.36, -0.7, 0.045], [-0.76, -0.04, 0.03], [0.1, 0.8, 0.03]];
  for (var p = 0; p < specks.length; p++) {
    var px = cx + specks[p][0] * sx, py = cy + specks[p][1] * sy, pr = specks[p][2] * sx;
    c.beginPath();
    c.ellipse(px, py, pr, pr * 0.8, 0, 0, TAU);
    c.fillStyle = STONE.speck;
    c.fill();
    c.beginPath();
    c.ellipse(px + pr * 0.3, py + pr * 0.45, pr * 0.7, pr * 0.32, 0, 0, Math.PI);
    c.strokeStyle = "rgba(241,231,216,0.7)";
    c.lineWidth = pr * 0.35;
    c.stroke();
  }
  // A crack on the right shoulder.
  c.beginPath();
  linePath(c, mapPts([[1.0, -0.42], [0.8, -0.34], [0.76, -0.14], [0.62, -0.06]], sx, sy, cx, cy));
  inkStyle(c, line * 0.5, INK);
  c.stroke();
  // Moss draped over the crown: a scalloped patch hanging from the top-left edge.
  var A = [cx - sx * 1.02, cy - sy * 0.3], B = [cx + sx * 0.24, cy - sy * 0.94];
  var n = 5, ux = (B[0] - A[0]) / n, uy = (B[1] - A[1]) / n;
  var seg = Math.hypot(ux, uy), nx = -uy / seg, ny = ux / seg;
  function drape(cc, k) {
    cc.moveTo(A[0], A[1]);
    for (var d = 0; d < n; d++) {
      var depth = (d % 2 ? 0.55 : 0.85) * seg * k;
      var mx = A[0] + ux * (d + 0.5) + nx * depth, my = A[1] + uy * (d + 0.5) + ny * depth;
      cc.quadraticCurveTo(mx, my, A[0] + ux * (d + 1), A[1] + uy * (d + 1));
    }
  }
  c.beginPath();
  drape(c, 1);
  c.lineTo(cx - sx * 1.5, cy - sy * 1.5);
  c.closePath();
  c.fillStyle = MOSS.shade;
  c.fill();
  c.save();
  c.translate(-sx * 0.05, -sy * 0.07);
  c.beginPath();
  drape(c, 0.9);
  c.lineTo(cx - sx * 1.5, cy - sy * 1.5);
  c.closePath();
  c.fillStyle = MOSS.base;
  c.fill();
  c.restore();
  c.fillStyle = MOSS.light;
  var tufts = [[-0.78, -0.5, 0.07], [-0.52, -0.74, 0.08], [-0.2, -0.88, 0.06], [-0.9, -0.28, 0.045]];
  for (var t = 0; t < tufts.length; t++) {
    c.beginPath();
    c.ellipse(cx + tufts[t][0] * sx, cy + tufts[t][1] * sy, tufts[t][2] * sx, tufts[t][2] * sx * 0.6, -0.5, 0, TAU);
    c.fill();
  }
  c.beginPath();
  drape(c, 1);
  inkStyle(c, line * 0.5, MOSS.ink);
  c.stroke();
  c.restore();

  c.beginPath();
  rock(c);
  inkStyle(c, line, INK);
  c.stroke();

  drawGrumpyFace(c, cx + sx * 0.02, cy + sy * 0.14, Math.min(sx, sy));
  angerMark(c, cx + sx * 0.5, cy - sy * 0.5, Math.min(sx, sy) * 0.17, line * 0.55);
  glint(c, cx - sx * 0.05, cy - sy * 0.62, Math.min(w, h) * 0.035);
  c.restore();
}

/**
 * The boulder's face: heavy brows slammed down over half-lidded eyes, a grumpy flush and a
 * clenched-teeth frown, so the hazard reads at a glance even while it tumbles.
 * @param {CanvasRenderingContext2D} c
 * @param {number} x face centre
 * @param {number} y
 * @param {number} s face scale (≈ rock radius)
 */
function drawGrumpyFace(c, x, y, s) {
  var gap = s * 0.3, ery = s * 0.17, erx = s * 0.13;
  // Grumpy flush under the eyes.
  c.save();
  c.fillStyle = "rgba(214, 96, 84, 0.32)";
  for (var b = -1; b <= 1; b += 2) {
    c.beginPath();
    c.ellipse(x + b * gap * 1.45, y + ery * 1.05, erx * 0.95, ery * 0.42, 0, 0, TAU);
    c.fill();
  }
  c.restore();
  for (var side = -1; side <= 1; side += 2) {
    var ex = x + side * gap;
    // Brow line: high at the outer end, low at the inner end (angry).
    var bx0 = ex + side * erx * 1.5, by0 = y - ery * 1.25;
    var bx1 = ex - side * erx * 1.2, by1 = y - ery * 0.3;
    c.save();
    // Only the part of the eye below the brow shows: a heavy lid. Clip to the half-plane
    // under the brow line, extended well past both ends.
    var dl = Math.hypot(bx1 - bx0, by1 - by0) || 1;
    var ux = (bx1 - bx0) / dl, uy = (by1 - by0) / dl;
    var ax = bx0 - ux * s, ay = by0 - uy * s, zx = bx1 + ux * s, zy = by1 + uy * s;
    c.beginPath();
    c.moveTo(ax, ay);
    c.lineTo(zx, zy);
    c.lineTo(zx, zy + s * 2);
    c.lineTo(ax, ay + s * 2);
    c.closePath();
    c.clip();
    c.beginPath();
    c.ellipse(ex, y, erx, ery, 0, 0, TAU);
    c.fillStyle = INK;
    c.fill();
    c.fillStyle = "#ffffff";
    c.beginPath();
    c.arc(ex - erx * 0.25 + side * erx * 0.1, y - ery * 0.05, erx * 0.38, 0, TAU);
    c.fill();
    c.beginPath();
    c.arc(ex + erx * 0.35, y + ery * 0.5, erx * 0.16, 0, TAU);
    c.fill();
    c.restore();
    c.beginPath();
    c.moveTo(bx0, by0);
    c.lineTo(bx1, by1);
    inkStyle(c, s * 0.11, INK);
    c.stroke();
  }
  // A frown of clenched teeth. Boulders spawn at any angle; upside down this turns into a
  // villain's toothy grin, so the face still says "hazard" however the rock lands.
  var my = y + s * 0.5, mw = s * 0.27;
  function mouth(cc) {
    cc.moveTo(x - mw, my + s * 0.1);
    cc.bezierCurveTo(x - mw * 0.7, my - s * 0.14, x + mw * 0.7, my - s * 0.14, x + mw, my + s * 0.1);
    cc.quadraticCurveTo(x, my + s * 0.02, x - mw, my + s * 0.1);
    cc.closePath();
  }
  c.beginPath();
  mouth(c);
  c.fillStyle = "#fffaf0";
  c.fill();
  c.save();
  c.clip();
  inkStyle(c, s * 0.035, INK);
  c.beginPath();
  c.moveTo(x - mw, my - s * 0.005);
  c.quadraticCurveTo(x, my - s * 0.06, x + mw, my - s * 0.005);
  for (var tth = -1; tth <= 1; tth += 2) {
    c.moveTo(x + tth * mw * 0.36, my - s * 0.12);
    c.lineTo(x + tth * mw * 0.36, my + s * 0.12);
  }
  c.stroke();
  c.restore();
  c.beginPath();
  mouth(c);
  inkStyle(c, s * 0.06, INK);
  c.stroke();
}

/**
 * The cartoon anger mark (four bowed ticks round a point), in warning red: it reads at any
 * angle, so a tumbling boulder never looks friendly.
 */
function angerMark(c, x, y, r, line) {
  c.save();
  c.translate(x, y);
  c.rotate(0.3);
  c.beginPath();
  // Each tick is a bracket whose corner points at the centre, turned a quarter per tick.
  for (var q = 0; q < 4; q++) {
    c.save();
    c.rotate(q * (Math.PI / 2));
    c.moveTo(r * 0.28, r);
    c.quadraticCurveTo(r * 0.2, r * 0.2, r, r * 0.28);
    c.restore();
  }
  c.lineCap = "round";
  c.lineJoin = "round";
  c.strokeStyle = INK;
  c.lineWidth = line * 2.4;
  c.stroke();
  c.strokeStyle = "#ff4b4b";
  c.lineWidth = line;
  c.stroke();
  c.restore();
}

// ---- gem -----------------------------------------------------------------------------------

var GEM = {
  lightest: "#ffc6fb",
  light: "#ff9cf5",
  mid: "#f06ee6",
  base: "#e24bd8",
  low: "#c535c0",
  shade: "#9c2699",
  seam: "rgba(90, 20, 96, 0.55)",
};

/** gem.png 128×113 (drawn square at min(w,h) in game) */
export function drawGem(c, w, h) {
  var hw = w * 0.45;
  var sy = hw * 0.94;
  var cx = w * 0.5;
  var oy = h * 0.5 - 0.2 * sy;
  var line = w * 0.055;
  function P(x, y) { return [cx + x * hw, oy + y * sy]; }
  var T0 = P(-0.56, -0.6), T3 = P(0.56, -0.6), G0 = P(-1, -0.14), G3 = P(1, -0.14);
  var G1 = P(-0.4, -0.14), G2 = P(0.4, -0.14), B = P(0, 1);
  var outline = [T0, T3, G3, B, G0];
  var facets = [
    [[T0, G1, G0], GEM.light],
    [[T0, T3, G2, G1], GEM.lightest],
    [[T3, G3, G2], GEM.base],
    [[G0, G1, B], GEM.mid],
    [[G1, G2, B], GEM.low],
    [[G2, G3, B], GEM.shade],
  ];
  c.save();
  c.lineJoin = "round";
  c.lineCap = "round";
  for (var i = 0; i < facets.length; i++) {
    c.beginPath();
    polyPath(c, facets[i][0], 1, 1, 0, 0);
    c.fillStyle = facets[i][1];
    c.fill();
    // Same-colour hairline closes the anti-aliasing seams between facets.
    c.strokeStyle = facets[i][1];
    c.lineWidth = 1;
    c.stroke();
  }
  // A lower-pavilion band a tone deeper: the cel crescent on the bottom right.
  c.save();
  c.beginPath();
  polyPath(c, outline, 1, 1, 0, 0);
  c.clip();
  c.beginPath();
  polyPath(c, [P(0.25, 0.75), P(1.2, -0.2), P(1.4, 1.3)], 1, 1, 0, 0);
  c.fillStyle = "rgba(110, 18, 120, 0.25)";
  c.fill();
  c.restore();
  // Facet seams.
  c.beginPath();
  c.moveTo(G0[0], G0[1]);
  c.lineTo(G3[0], G3[1]);
  c.moveTo(T0[0], T0[1]);
  c.lineTo(G1[0], G1[1]);
  c.lineTo(B[0], B[1]);
  c.lineTo(G2[0], G2[1]);
  c.lineTo(T3[0], T3[1]);
  inkStyle(c, line * 0.45, GEM.seam);
  c.stroke();
  // Sheen on the table and the left crown facet.
  c.beginPath();
  polyPath(c, [P(-0.36, -0.52), P(-0.12, -0.52), P(-0.3, -0.22), P(-0.52, -0.22)], 1, 1, 0, 0);
  c.fillStyle = "rgba(255,255,255,0.9)";
  c.fill();
  c.beginPath();
  polyPath(c, [P(-0.62, 0.02), P(-0.48, 0.02), P(-0.2, 0.42), P(-0.3, 0.46)], 1, 1, 0, 0);
  c.fillStyle = "rgba(255,255,255,0.55)";
  c.fill();
  // Outline.
  c.beginPath();
  polyPath(c, outline, 1, 1, 0, 0);
  inkStyle(c, line, INK);
  c.stroke();
  // Sparkle off the top-right corner.
  var spx = cx + hw * 0.8, spy = oy - sy * 0.5, spr = w * 0.1;
  c.beginPath();
  sparklePath(c, spx, spy, spr, 0);
  c.fillStyle = "#ffffff";
  c.fill();
  inkStyle(c, line * 0.42, INK);
  c.stroke();
  c.restore();
}

// ---- shield --------------------------------------------------------------------------------

var GOLD = { light: "#fff1a8", base: "#ffc94a", shade: "#e08a1c", deep: "#b8650f" };
var HONEY = { gap: "#c9561a", light: "#ffd36e", base: "#ffaa3c", shade: "#f2832a" };

function shieldPath(c, w, h, k) {
  // Heater shield with a two-hump crest; `k` shrinks it about its centre (for the inner face).
  var cx = w * 0.5, cy = h * 0.47;
  function X(x) { return cx + (x - cx) * k; }
  function Y(y) { return cy + (y - cy) * k; }
  var top = h * 0.12, crest = h * 0.07, mid = h * 0.1, side = w * 0.095, low = h * 0.93;
  c.moveTo(X(side), Y(top + h * 0.03));
  c.quadraticCurveTo(X(side), Y(top), X(side + w * 0.06), Y(top - h * 0.01));
  c.quadraticCurveTo(X(w * 0.3), Y(crest), X(cx), Y(mid));
  c.quadraticCurveTo(X(w * 0.7), Y(crest), X(w - side - w * 0.06), Y(top - h * 0.01));
  c.quadraticCurveTo(X(w - side), Y(top), X(w - side), Y(top + h * 0.03));
  c.bezierCurveTo(X(w - side + w * 0.01), Y(h * 0.56), X(w * 0.78), Y(h * 0.8), X(cx), Y(low));
  c.bezierCurveTo(X(w * 0.22), Y(h * 0.8), X(side - w * 0.01), Y(h * 0.56), X(side), Y(top + h * 0.03));
  c.closePath();
}

function hexPath(c, x, y, r) {
  for (var i = 0; i < 6; i++) {
    var a = Math.PI / 6 + (i / 6) * TAU;
    var px = x + Math.cos(a) * r, py = y + Math.sin(a) * r;
    if (i) c.lineTo(px, py); else c.moveTo(px, py);
  }
  c.closePath();
}

/** shield.png 128×141 */
export function drawShield(c, w, h) {
  var line = w * 0.06;
  var cx = w * 0.5, cy = h * 0.47;
  c.save();
  c.lineJoin = "round";
  c.lineCap = "round";
  // Gold rim: shade, lit body shifted up-left, light cap.
  c.beginPath();
  shieldPath(c, w, h, 1);
  c.fillStyle = GOLD.shade;
  c.fill();
  c.save();
  c.clip();
  c.beginPath();
  c.translate(-w * 0.04, -h * 0.035);
  shieldPath(c, w, h, 0.98);
  c.fillStyle = GOLD.base;
  c.fill();
  c.restore();

  // Honeycomb face.
  var k = 0.74;
  c.save();
  c.beginPath();
  shieldPath(c, w, h, k);
  c.fillStyle = HONEY.gap;
  c.fill();
  c.clip();
  var r = w * 0.115, dx = Math.sqrt(3) * r, dy = 1.5 * r;
  for (var row = -4; row <= 4; row++) {
    for (var col = -4; col <= 4; col++) {
      var hx = cx + col * dx + (row & 1 ? dx / 2 : 0), hy = cy + h * 0.02 + row * dy;
      var d = (hx - cx) * 0.55 + (hy - cy) * 0.85;
      c.beginPath();
      hexPath(c, hx, hy, r - w * 0.022);
      c.fillStyle = d < -r * 1.1 ? HONEY.light : d > r * 1.2 ? HONEY.shade : HONEY.base;
      c.fill();
    }
  }
  // Glossy diagonal sheen across the upper-left of the face.
  c.beginPath();
  c.moveTo(0, h * 0.5);
  c.lineTo(w * 0.62, 0);
  c.lineTo(w * 0.8, 0);
  c.lineTo(0, h * 0.66);
  c.closePath();
  c.fillStyle = "rgba(255,255,255,0.3)";
  c.fill();
  c.restore();
  c.beginPath();
  shieldPath(c, w, h, k);
  inkStyle(c, line * 0.55, INK);
  c.stroke();

  // Lit rim edge along the upper left of the gold band.
  c.save();
  c.beginPath();
  shieldPath(c, w, h, 1);
  c.clip();
  c.beginPath();
  c.moveTo(w * 0.15, h * 0.62);
  c.lineTo(w * 0.15, h * 0.17);
  c.quadraticCurveTo(w * 0.32, h * 0.1, w * 0.47, h * 0.15);
  c.strokeStyle = GOLD.light;
  c.lineWidth = line * 0.9;
  c.stroke();
  c.restore();

  // Rivets on the rim.
  var rivets = [[0.17, 0.16], [0.83, 0.16], [0.5, 0.86]];
  for (var i = 0; i < rivets.length; i++) {
    var rx = w * rivets[i][0], ry = h * rivets[i][1], rr = w * 0.042;
    c.beginPath();
    c.arc(rx, ry, rr, 0, TAU);
    c.fillStyle = GOLD.deep;
    c.fill();
    c.beginPath();
    c.arc(rx - rr * 0.25, ry - rr * 0.25, rr * 0.62, 0, TAU);
    c.fillStyle = GOLD.light;
    c.fill();
  }

  c.beginPath();
  shieldPath(c, w, h, 1);
  inkStyle(c, line, INK);
  c.stroke();
  glint(c, w * 0.3, h * 0.3, w * 0.05);
  c.restore();
}

// ---- buoy ----------------------------------------------------------------------------------

var BUOY_RED = { light: "#ff9b9b", base: "#ff5a5f", shade: "#d2363f" };
var BUOY_WHITE = { light: "#ffffff", base: "#f2f6fa", shade: "#c3d2df" };

/**
 * A fishing-float buoy, like buoy.png: a red dome with a tab on top, a rolled red lip at the
 * waist and a white bowl underneath.
 * buoy.png 300×300
 */
export function drawBuoy(c, w, h) {
  var s = Math.min(w, h);
  var cx = w * 0.5;
  var line = s * 0.05;
  var waist = h * 0.6;
  var domeRx = s * 0.38, domeRy = s * 0.38;
  var lipH = s * 0.12, lipRx = s * 0.415;
  var bowlRx = s * 0.36, bowlRy = s * 0.3;
  var bowlTop = waist + lipH * 0.5;
  c.save();
  c.lineJoin = "round";
  c.lineCap = "round";

  // Tab with a hole, behind the dome.
  var tabW = s * 0.21, tabTop = waist - domeRy - s * 0.17;
  c.beginPath();
  roundRectPath(c, cx - tabW / 2, tabTop, tabW, s * 0.26, tabW / 2);
  c.moveTo(cx + s * 0.042, tabTop + tabW / 2);
  c.arc(cx, tabTop + tabW / 2, s * 0.042, 0, TAU, true);
  c.fillStyle = BUOY_RED.base;
  c.fill("evenodd");
  inkStyle(c, line * 0.75, INK);
  c.stroke();

  // White bowl.
  function bowl(cc) {
    cc.moveTo(cx - bowlRx, bowlTop);
    cc.ellipse(cx, bowlTop, bowlRx, bowlRy, 0, Math.PI, 0, true);
    cc.closePath();
  }
  c.beginPath();
  bowl(c);
  c.fillStyle = BUOY_WHITE.shade;
  c.fill();
  c.save();
  c.clip();
  c.beginPath();
  c.ellipse(cx - bowlRx * 0.12, bowlTop - bowlRy * 0.1, bowlRx * 0.94, bowlRy * 0.94, 0, 0, TAU);
  c.fillStyle = BUOY_WHITE.base;
  c.fill();
  c.restore();
  c.beginPath();
  bowl(c);
  inkStyle(c, line, INK);
  c.stroke();

  // Red dome.
  function dome(cc) {
    cc.moveTo(cx - domeRx, waist);
    cc.ellipse(cx, waist, domeRx, domeRy, 0, Math.PI, 0);
    cc.closePath();
  }
  c.beginPath();
  dome(c);
  c.fillStyle = BUOY_RED.shade;
  c.fill();
  c.save();
  c.clip();
  c.beginPath();
  c.ellipse(cx - domeRx * 0.1, waist + domeRy * 0.02, domeRx * 0.93, domeRy * 0.97, 0, 0, TAU);
  c.fillStyle = BUOY_RED.base;
  c.fill();
  c.beginPath();
  c.ellipse(cx - domeRx * 0.42, waist - domeRy * 0.6, domeRx * 0.3, domeRy * 0.18, -0.65, 0, TAU);
  c.fillStyle = BUOY_RED.light;
  c.fill();
  c.restore();
  c.beginPath();
  dome(c);
  inkStyle(c, line, INK);
  c.stroke();

  // Rolled lip at the waist: a red band a little wider than both halves.
  c.beginPath();
  roundRectPath(c, cx - lipRx, waist - lipH * 0.5, lipRx * 2, lipH, lipH * 0.5);
  c.fillStyle = BUOY_RED.shade;
  c.fill();
  c.save();
  c.clip();
  c.fillStyle = BUOY_RED.base;
  c.fillRect(cx - lipRx, waist - lipH * 0.5, lipRx * 2 - s * 0.05, lipH * 0.62);
  c.fillStyle = BUOY_RED.light;
  c.fillRect(cx - lipRx, waist - lipH * 0.5, lipRx * 1.2, lipH * 0.22);
  c.restore();
  c.beginPath();
  roundRectPath(c, cx - lipRx, waist - lipH * 0.5, lipRx * 2, lipH, lipH * 0.5);
  inkStyle(c, line * 0.8, INK);
  c.stroke();

  glint(c, cx - domeRx * 0.55, waist - domeRy * 0.55, s * 0.045);
  c.restore();
}
